import { TableContainer } from '@mui/material';

import { type Dispatch, type SetStateAction } from 'react';

import { useDndContext } from '@dnd-kit/core';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import type { OwnerPermisson } from '../../hooks/useFilePermissions';

import { FileManagerEmptyFolder } from './content/FileManagerEmptyFolder';
import { FileManagerSearchResultTable } from './content/FileManagerSearchResultTable';
import { FileManagerFileNotFound } from './content/FileManagerFileNotFound';
import { FileView } from './content/FileView';
import { FileManagerEmptyRootFolder } from './content/FileManagerEmptyRootFolder';
import type { FileManagerUploadingState } from './FileManagerUploadingBar';
import { FileManagerContentSkeleton } from './content/FileManagerContentSkeleton';
import { FileManagerContentTable } from './content/FileManagerContentTable';

export const FILE_MANAGER_CONTENT_FRAGMENT = graphql(`
  fragment fileManagerContentFields on FilesForOwner {
    ...fileManagerSearchResultTableFields
    ...fileViewFilesForOwnerFields
    ...fileManagerEmptyRootFolderFilesForOwnerFields
    ...fileManagerContentTableFilesForOwnerFields
    parent {
      _exists
      _id
      breadcrumbs {
        file {
          ...fileFields
        }
      }
    }
    filesConnection {
      edges {
        node {
          ...fileFields
        }
      }
    }
  }
`);
interface Props {
  loading: boolean;
  searchText: string;
  searchMode: boolean;
  filesForOwner:
    DocumentType<typeof FILE_MANAGER_CONTENT_FRAGMENT> | null | undefined;
  ownerPermissions: OwnerPermisson[];
  rootOwnerId: string;
  sort: string | undefined;
  allSelected: boolean;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

export const FileManagerContent = ({
  loading,
  searchMode,
  searchText,
  filesForOwner,
  ownerPermissions,
  rootOwnerId,
  sort,
  allSelected,
  setUploadingState,
}: Props) => {
  const { active } = useDndContext();

  const isDragging = Boolean(active);

  const { filesConnection } = filesForOwner ?? {};

  const files = extractConnectionProps(filesConnection);

  if (searchText && searchMode && filesForOwner) {
    return (
      <FileManagerSearchResultTable
        filesForOwner={filesForOwner}
        searchText={searchText}
      />
    );
  }

  const parent = filesForOwner?.parent;

  if (parent && !parent._exists) {
    return <FileManagerFileNotFound />;
  }

  if (parent && !parent.folder) {
    return <FileView filesForOwner={filesForOwner} />;
  }

  if (files.length === 0 && loading) {
    return <FileManagerContentSkeleton />;
  }

  if (files.length === 0 && filesForOwner) {
    return parent ? (
      <FileManagerEmptyFolder />
    ) : (
      <FileManagerEmptyRootFolder filesForOwner={filesForOwner} />
    );
  }

  const contentTable = (
    <FileManagerContentTable
      filesForOwner={filesForOwner}
      sort={sort}
      ownerPermissions={ownerPermissions}
      rootOwnerId={rootOwnerId}
      allSelected={allSelected}
      setUploadingState={setUploadingState}
    />
  );

  if (isDragging) {
    return contentTable;
  }

  return <TableContainer>{contentTable}</TableContainer>;
};
